import React, { memo, useEffect, useState } from 'react';
import { designSystem } from '@yola/ws-ui';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { t } from 'i18next';
import isMatchMobile from 'src/js/helpers/is-match-mobile';
import ConditionalWrapper from 'src/js/common/components/conditional-wrapper';

const { ActionButton, Heading, Box, ActionButtonGroup, Container, Stack } = designSystem;

const defaultNext = {
  callback: null,
  disabled: false,
};

const defaultSkip = {
  callback: null,
};

function SiteSetupLayout({
  captions,
  children = null,
  className = '',
  next = defaultNext,
  skip = defaultSkip,
  currentStep,
  isLoading = false,
  isStepCounter = true,
  areButtonsOnSeparateRow = true,
}) {
  const { title, subtitle, buttons } = captions;
  const siteSetupLayoutClassName = classnames('ws-site-setup-layout', className);

  const [isMobile, setIsMobile] = useState(isMatchMobile());

  useEffect(() => {
    const resize = () => setIsMobile(isMatchMobile());

    window.addEventListener('resize', resize);

    return () => window.removeEventListener('resize', resize);
  }, []);

  const buttonsMarkup = (
    <ConditionalWrapper
      condition={areButtonsOnSeparateRow || isMobile}
      wrapper={(wrapperChildren) => <ActionButtonGroup>{wrapperChildren}</ActionButtonGroup>}
    >
      <>
        {next.callback && (
          <ActionButton
            appearance="accent"
            format="solid"
            size="large"
            isLoading={isLoading}
            label={buttons?.next}
            onClick={next.callback}
            fullWidth={isMobile}
            disabled={next.disabled}
          />
        )}
        {skip.callback && (
          <ActionButton
            format="text"
            size="large"
            label={buttons?.skip}
            onClick={skip.callback}
            fullWidth={isMobile}
          />
        )}
      </>
    </ConditionalWrapper>
  );

  return (
    <div className={siteSetupLayoutClassName}>
      <div>
        <Container>
          <Box breakpoints={{ xl: { paddingTop: 'spacing-xs' } }}>
            <Box
              paddingTop="spacing-xl"
              paddingBottom="spacing-xl"
              breakpoints={{
                md: { paddingTop: 'spacing-3-xl' },
                xl: { paddingBottom: 'spacing-2-xl' },
              }}
            >
              <Stack
                gap="spacing-3-xs"
                breakpoints={{
                  md: { gap: 'spacing-2-xs' },
                  xl: { gap: 'spacing-xs' },
                }}
              >
                {isStepCounter && (
                  <Heading type="heading-6" appearance="accent">
                    {t('Step {current}', {
                      current: currentStep,
                    })}
                  </Heading>
                )}
                <Heading type="heading-1">{title}</Heading>
                {subtitle && <Heading type="heading-5">{subtitle}</Heading>}
              </Stack>
            </Box>
          </Box>
        </Container>
        <div>
          {areButtonsOnSeparateRow ? (
            <>
              {children}
              <Container>{buttonsMarkup}</Container>
            </>
          ) : (
            <Container
              className={classnames({
                'ws-site-setup-layout__container ws-site-setup-layout__container--one-row':
                  !isMobile,
              })}
            >
              {children}
              {buttonsMarkup}
            </Container>
          )}
        </div>
      </div>
    </div>
  );
}

SiteSetupLayout.propTypes = {
  captions: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    buttons: PropTypes.shape({
      next: PropTypes.string,
      skip: PropTypes.string,
    }),
  }).isRequired,
  children: PropTypes.node,
  currentStep: PropTypes.number.isRequired,
  next: PropTypes.shape({
    callback: PropTypes.func,
    disabled: PropTypes.bool,
  }),
  skip: PropTypes.shape({
    callback: PropTypes.func,
  }),
  isLoading: PropTypes.bool,
  isStepCounter: PropTypes.bool,
  className: PropTypes.string,
  areButtonsOnSeparateRow: PropTypes.bool,
};

export default memo(SiteSetupLayout);
